import {
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Input,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Observable } from 'rxjs';

import { AsyncPipe, NgClass } from '@angular/common';
import { Store } from '@ngrx/store';
import { selectIsArabic } from '../store/selectors/language.selectors';
import { SavedSearchSummary } from './saved-search-summary.service';
import { CreateSavedSearch, SavedSearchDomain } from './saved-search.model';
import { SavedSearchService } from './saved-search.service';

@Component({
  selector: 'app-save-search',
  standalone: true,
  imports: [TranslateModule, AsyncPipe, NgClass],
  templateUrl: './save-search.component.html',
  styleUrls: ['./saved-search.scss'],
})
export class SaveSearchComponent {
  @Input({ required: true }) form!: FormGroup;
  @Input({ required: true }) domain!: SavedSearchDomain;
  @ViewChild('dialog') dialog?: ElementRef<HTMLDialogElement>;
  private readonly store = inject(Store);
  readonly service = inject(SavedSearchService);
  readonly summary = inject(SavedSearchSummary);
  readonly translate = inject(TranslateService);
  private readonly destroy = inject(DestroyRef);
  private owner?: number;
  private static sequence = 0;
  readonly titleId = 'save-search-title-' + ++SaveSearchComponent.sequence;
  readonly request = signal<CreateSavedSearch | null>(null);
  readonly opened = signal(false);
  readonly pending = signal(false);
  readonly feedback = signal('');
  readonly error = signal('');

  isArabic$: Observable<boolean> = this.store.select(selectIsArabic);

  constructor() {
    effect(() => {
      const user = this.service.user();
      if (this.owner != null && user?.id !== this.owner) {
        this.request.set(null);
        this.opened.set(false);
        this.dialog?.nativeElement.close();
        this.feedback.set('');
        this.error.set('');
      }
    });
  }
  open(): void {
    this.feedback.set('');
    this.error.set('');
    if (!this.service.authorize()) {
      if (this.service.user()) this.feedback.set('SAVED_SEARCH.eligible');
      return;
    }
    if (this.form.invalid) {
      this.feedback.set('SAVED_SEARCH.invalid');
      return;
    }
    this.owner = this.service.user()!.id;
    this.request.set(this.service.snapshot(this.domain, this.form.getRawValue()));
    this.opened.set(true);
    this.dialog?.nativeElement.showModal();
  }
  close(): void {
    if (this.pending()) return;
    this.opened.set(false);
    this.dialog?.nativeElement.close();
    this.request.set(null);
  }
  cancel(event: Event): void {
    if (this.pending()) event.preventDefault();
    else this.close();
  }
  async confirm(): Promise<void> {
    const request = this.request();
    if (!request || this.pending()) return;
    if (this.service.user()?.id !== this.owner || !this.service.authorize()) {
      this.error.set('SAVED_SEARCH.eligible');
      return;
    }
    this.pending.set(true);
    this.error.set('');
    try {
      const response = await firstValueFrom(
        this.service.create(request).pipe(takeUntilDestroyed(this.destroy))
      );
      if (this.service.user()?.id !== this.owner) return;
      this.feedback.set(
        response.body?.reactivated ? 'SAVED_SEARCH.reactivated' : response.status === 201 ? 'SAVED_SEARCH.success' : 'SAVED_SEARCH.duplicate'
      );
      this.pending.set(false);
      this.close();
    } catch (error) {
      this.error.set(this.service.errorKey(error));
    } finally {
      this.pending.set(false);
    }
  }
}
